import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Member } from '../../api/groups';
import ModalContext from '../../contexts/ModalContext';
import Dialog from '@pelckmans/elementis/components/dialog';
import SearchField from '@pelckmans/elementis/components/search-field';
import Button from '@pelckmans/elementis/components/button';
import List from '@pelckmans/elementis/components/list';
import keyBy from 'lodash/keyBy';
import GroupListHierarchy, { CheckboxState } from './GroupListHierarchy';
import { useBuildList } from './useBuildList';

export const MODAL_ID = 'filter-students-modal';

interface DetermineStateOpts {
  allMembersOfGroup: { id: string }[];
  checkedMembersOfGroup: string[];
}

function determineCheckboxStateForNode({
  allMembersOfGroup,
  checkedMembersOfGroup,
}: DetermineStateOpts): CheckboxState {
  if (checkedMembersOfGroup.length === 0) return 'unchecked';
  if (allMembersOfGroup.length === checkedMembersOfGroup.length) return 'checked';

  return 'indeterminate';
}

export interface FilterStudentsGroup {
  id: string;
  name: string;
  acceptedMemberCount: number;
}

interface Props {
  id: string;
  translationKeyBase: string;
  groups: FilterStudentsGroup[];
  selectedGroups: FilterStudentsGroup[];
  membersPerGroup: Record<string, Member[]>;
  selectedMembersPerGroup: Record<string, Member[]>;
  setSelectedMembers: (members: Record<string, string[]>) => void;
}

export default function FilterStudentsModal({
  id,
  translationKeyBase,
  groups,
  selectedGroups,
  membersPerGroup,
  selectedMembersPerGroup,
  setSelectedMembers,
}: Props) {
  const groupsById = useMemo(
    () =>
      keyBy(
        groups.filter(g => g.acceptedMemberCount > 0),
        'id',
      ),
    [groups],
  );

  const { t } = useTranslation();

  const emptyState = useMemo(() => {
    return Object.keys(membersPerGroup).reduce((acc, groupId) => {
      acc[groupId] = [] as string[];
      return acc;
    }, {} as Record<string, string[]>);
  }, [membersPerGroup]);

  const [checkedMemberIdsPerGroup, setCheckedMemberIdsPerGroup] = useState(() =>
    Object.keys(membersPerGroup).reduce((acc, groupId) => {
      acc[groupId] = (selectedMembersPerGroup[groupId] || []).map(member => member.id);
      return acc;
    }, {} as Record<string, string[]>),
  );

  const { searchValue, filterList, setSearchValue, list, isFilterEnabled, clearSearch } = useBuildList({
    membersPerGroup,
    groupsById,
  });

  const groupCheckboxStates = useMemo<Record<string, CheckboxState>>(() => {
    const res = list.reduce((acc, group) => {
      acc[group.id] = determineCheckboxStateForNode({
        allMembersOfGroup: group.members,
        checkedMembersOfGroup: checkedMemberIdsPerGroup[group.id] || [],
      });

      return acc;
    }, {} as Record<string, CheckboxState>);

    return res;
  }, [checkedMemberIdsPerGroup, list]);

  const { closeModal } = useContext(ModalContext);

  const handleClose = () => {
    closeModal(id);
  };

  function handleMemberClick(memberId: string, groupId: string) {
    const memberIsChecked = checkedMemberIdsPerGroup[groupId].includes(memberId);

    const newSelectedMembers = {
      ...checkedMemberIdsPerGroup,
      [groupId]: memberIsChecked
        ? checkedMemberIdsPerGroup[groupId].filter(id => id !== memberId)
        : [...checkedMemberIdsPerGroup[groupId], memberId],
    };

    setCheckedMemberIdsPerGroup(newSelectedMembers);
    filterList(newSelectedMembers, isFilterEnabled);
  }

  function getMemberCheckboxState(memberId: string, groupId: string): CheckboxState {
    return checkedMemberIdsPerGroup[groupId].includes(memberId) ? 'checked' : 'unchecked';
  }

  function handleGroupClick(groupId: string) {
    const currentGroupCheckboxState = groupCheckboxStates[groupId];

    let targetCheckboxState: CheckboxState = 'checked';
    if (currentGroupCheckboxState === 'checked') {
      targetCheckboxState = 'unchecked';
    }

    setCheckedMemberIdsPerGroup(prev => {
      if (targetCheckboxState === 'unchecked') {
        return {
          ...prev,
          [groupId]: [],
        };
      }

      const newCheckedMembers = list.find(g => g.id === groupId)?.members.map(m => m.id) || [];

      return {
        ...prev,
        [groupId]: newCheckedMembers,
      };
    });
  }

  function handleSelectAll() {
    const allSelectedMemberIdsPerGroup = list.reduce((acc, group) => {
      acc[group.id] = group.members.map(m => m.id);
      return acc;
    }, {} as Record<string, string[]>);

    setCheckedMemberIdsPerGroup(allSelectedMemberIdsPerGroup);
  }

  function handleClearSelection() {
    const newCheckedMemberIds = emptyState;

    setCheckedMemberIdsPerGroup(newCheckedMemberIds);
    filterList(newCheckedMemberIds, Boolean(searchValue));
  }

  return (
    <Dialog id={id} modal size="sm" onClose={handleClose} aria-label={t(`${translationKeyBase}.A11Y_LABEL`)}>
      <Dialog.Header
        title={t(`${translationKeyBase}.HEADER.TITLE`)}
        onClose={handleClose}
        aria-label={t(`${translationKeyBase}.HEADER.A11Y_LABEL`)}
      >
        <SearchField
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onClear={clearSearch}
          onSearch={() => filterList(checkedMemberIdsPerGroup, Boolean(searchValue))}
          aria-label={t(`${translationKeyBase}.HEADER.SEARCH.A11Y_LABEL`)}
          clearActionLabel={t(`${translationKeyBase}.HEADER.SEARCH.CLEAR.A11Y_LABEL`)}
          placeholder={t(`${translationKeyBase}.HEADER.SEARCH.PLACEHOLDER`)}
          searchActionLabel={t(`${translationKeyBase}.HEADER.SEARCH.SEARCH_BUTTON.LABEL`)}
        />
      </Dialog.Header>
      <Dialog.Body>
        <List>
          {list.map(group => (
            <GroupListHierarchy
              translationKeyBase={translationKeyBase}
              group={group}
              groupCheckboxState={groupCheckboxStates[group.id]}
              members={group.members}
              defaultCollapsed={selectedGroups.length !== 1 && !isFilterEnabled}
              handleMemberClick={handleMemberClick}
              getCheckboxState={getMemberCheckboxState}
              handleGroupClick={handleGroupClick}
              key={group.id}
            />
          ))}
        </List>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={{
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button key="select-all" prominence="ghost" onClick={handleSelectAll}>
                {t('ACTIONS.SELECT_ALL')}
              </Button>
              <Button
                key="deselect-all"
                prominence="ghost"
                disabled={Object.values(groupCheckboxStates).every(state => state === 'unchecked')}
                onClick={handleClearSelection}
              >
                {t('ACTIONS.CLEAR_SELECTION')}
              </Button>
            </>
          ),
        }}
        buttonGroupPropsRight={{
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="close">
                {t('ACTIONS.CLOSE')}
              </Button>
              <Button
                key="apply"
                prominence="success"
                onClick={() => {
                  setSelectedMembers(checkedMemberIdsPerGroup);
                  handleClose();
                }}
              >
                {t('ACTIONS.APPLY')}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
